import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Share2, MessageCircle, ArrowRightLeft, CheckCircle2, FileText, ReceiptText, Users, Copy, Printer } from 'lucide-react';
import { toast } from 'sonner';

const DOC_META = {
  invoice: {
    title: 'Invoices',
    endpoint: 'invoices',
    feature: 'can_create_invoices',
    receiptFeature: 'can_use_receipts',
    itemLabel: 'Invoice item',
    statusOptions: ['draft', 'sent', 'unpaid', 'paid', 'overdue', 'cancelled'],
    showDueDate: true,
    primaryAction: 'Pay Online',
    secondaryAction: 'WhatsApp',
  },
  quotation: {
    title: 'Quotations',
    endpoint: 'quotations',
    feature: 'can_create_quotations',
    receiptFeature: 'can_use_receipts',
    itemLabel: 'Quotation item',
    statusOptions: ['draft', 'sent', 'accepted', 'rejected', 'converted_to_invoice'],
    showDueDate: true,
    primaryAction: 'Convert',
    secondaryAction: 'WhatsApp',
  },
  receipt: {
    title: 'Receipts',
    endpoint: 'receipts',
    feature: 'can_use_receipts',
    receiptFeature: 'can_use_receipts',
    itemLabel: 'Receipt',
    statusOptions: ['all'],
    showDueDate: false,
  },
  customer: {
    title: 'Customers',
    endpoint: 'customers',
    feature: 'can_use_crm',
    receiptFeature: 'can_use_crm',
    itemLabel: 'Customer',
    statusOptions: ['all'],
    showDueDate: false,
  },
};

const EMPTY_LINE_ITEM = {
  id: '',
  name: '',
  description: '',
  quantity: 1,
  unit_price: '',
  total: '',
  sort_order: 1,
};

function formatMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid' || s === 'accepted') return 'bg-emerald-100 text-emerald-800';
  if (s === 'sent' || s === 'unpaid' || s === 'draft') return 'bg-amber-100 text-amber-800';
  if (s === 'overdue' || s === 'rejected' || s === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

function statusText(status) {
  return String(status || '-').replace(/_/g, ' ');
}

function fieldValue(doc, field, fallback = '') {
  return doc && doc[field] != null ? doc[field] : fallback;
}

export default function CommerceDocsPage({ mode }) {
  const config = DOC_META[mode] || DOC_META.invoice;
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE_ITEM, id: `item-${Date.now()}` }]);
  const [form, setForm] = useState({
    title: '',
    customer_name: '',
    customer_company: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    customer_notes: '',
    currency: 'UGX',
    status: 'draft',
    due_date: '',
    validity_date: '',
    discount: '',
    tax: '',
    notes: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
  });

  const access = data?.access || {};
  const canEdit = mode === 'invoice'
    ? access?.can_create_invoices && access?.can_use_receipts
    : mode === 'quotation'
      ? access?.can_create_quotations && access?.can_use_receipts
      : mode === 'receipt'
        ? access?.can_use_receipts
        : access?.can_create_invoices || access?.can_use_crm;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard/${config.endpoint}`);
      setData(res.data || {});
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || 'Failed to load data';
      toast.error(detail);
      setData({ items: [], access: err.response?.status === 403 ? { ...access } : {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const items = useMemo(() => {
    if (mode === 'invoice') return data.invoices || [];
    if (mode === 'quotation') return data.quotations || [];
    if (mode === 'receipt') return data.receipts || [];
    return data.customers || [];
  }, [data, mode]);

  useEffect(() => {
    if (mode === 'customer' || mode === 'receipt') return;
    const selected = items.find((item) => item.id === selectedId);
    if (!selected) return;
    setForm({
      title: fieldValue(selected, 'title', ''),
      customer_name: selected.customer?.name || fieldValue(selected, 'customer_name', ''),
      customer_company: selected.customer?.company || '',
      customer_phone: selected.customer?.phone || fieldValue(selected, 'customer_phone', ''),
      customer_email: selected.customer?.email || fieldValue(selected, 'customer_email', ''),
      customer_address: selected.customer?.address || fieldValue(selected, 'customer_address', ''),
      customer_notes: selected.customer?.notes || '',
      currency: fieldValue(selected, 'currency', 'UGX'),
      status: fieldValue(selected, 'status', 'draft'),
      due_date: fieldValue(selected, 'due_date', ''),
      validity_date: fieldValue(selected, 'validity_date', ''),
      discount: fieldValue(selected, 'discount', ''),
      tax: fieldValue(selected, 'tax', ''),
      notes: fieldValue(selected, 'notes', ''),
      name: fieldValue(selected, 'name', ''),
      email: fieldValue(selected, 'email', ''),
      phone: fieldValue(selected, 'phone', ''),
      address: fieldValue(selected, 'address', ''),
      company: fieldValue(selected, 'company', ''),
    });
    const docItems = Array.isArray(selected.items) && selected.items.length
      ? selected.items.map((item, index) => ({
          id: item.id || `item-${index}-${Date.now()}`,
          name: item.name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price != null ? item.unit_price : '',
          total: item.total != null ? item.total : '',
          sort_order: item.sort_order || index + 1,
        }))
      : [{ ...EMPTY_LINE_ITEM, id: `item-${Date.now()}` }];
    setLineItems(docItems);
  }, [selectedId, items, mode]);

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateLine = (index, key, value) => {
    setLineItems((prev) => prev.map((item, idx) => {
      if (idx !== index) return item;
      const next = { ...item, [key]: value };
      if (key === 'quantity' || key === 'unit_price') {
        const qty = Number(next.quantity || 0);
        const price = Number(next.unit_price || 0);
        next.total = Number.isNaN(qty * price) ? '' : (qty * price).toFixed(2);
      }
      return next;
    }));
  };
  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE_ITEM, id: `item-${Date.now()}-${prev.length}` }]);
  const removeLine = (index) => setLineItems((prev) => prev.filter((_, idx) => idx !== index));

  const resetForm = () => {
    setSelectedId('');
    setLineItems([{ ...EMPTY_LINE_ITEM, id: `item-${Date.now()}` }]);
    setForm({
      title: '',
      customer_name: '',
      customer_company: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      customer_notes: '',
      currency: 'UGX',
      status: 'draft',
      due_date: '',
      validity_date: '',
      discount: '',
      tax: '',
      notes: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      company: '',
    });
  };

  const preparePayload = () => {
    const payload = { ...form };
    const normalizedItems = lineItems
      .map((item, index) => ({
        id: item.id || `item-${index}-${Date.now()}`,
        name: String(item.name || '').trim(),
        description: String(item.description || ''),
        quantity: Number(item.quantity || 1) || 1,
        unit_price: Number(item.unit_price || 0) || 0,
        total: Number(item.total || 0) || 0,
        sort_order: index + 1,
      }))
      .filter((item) => item.name);
    payload.items = normalizedItems;
    payload.discount = Number(payload.discount || 0) || 0;
    payload.tax = Number(payload.tax || 0) || 0;
    if (mode === 'customer') {
      payload.customer_name = String(payload.name || '').trim();
      payload.customer_company = String(payload.company || '').trim();
      payload.customer_phone = String(payload.phone || '').trim();
      payload.customer_email = String(payload.email || '').trim();
      payload.customer_address = String(payload.address || '').trim();
      payload.customer_notes = String(payload.notes || '').trim();
    }
    if (mode === 'invoice') {
      payload.customer_name = String(payload.customer_name || '').trim();
      payload.customer_company = String(payload.customer_company || '').trim();
      payload.customer_phone = String(payload.customer_phone || '').trim();
      payload.customer_email = String(payload.customer_email || '').trim();
      payload.customer_address = String(payload.customer_address || '').trim();
      payload.customer_notes = String(payload.customer_notes || '').trim();
    }
    return payload;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canEdit) return toast.error('Upgrade your package to use this feature.');
    if (mode !== 'receipt' && mode !== 'customer' && lineItems.filter((item) => String(item.name || '').trim()).length === 0) {
      return toast.error(`Add at least one ${config.itemLabel.toLowerCase()}.`);
    }
    setSaving(true);
    try {
      const payload = preparePayload();
      let res;
      if (selectedId) {
        res = await api.put(`/dashboard/${config.endpoint}/${selectedId}`, payload);
      } else {
        res = await api.post(`/dashboard/${config.endpoint}`, payload);
      }
      toast.success(res.data?.message || `${config.title} saved`);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || `Failed to save ${config.title.toLowerCase()}`);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Save failed', err.response?.status, err.response?.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const editDoc = (doc) => {
    if (mode === 'customer') {
      setForm({
        title: '',
        customer_name: '',
        customer_company: '',
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        customer_notes: '',
        currency: 'UGX',
        status: 'draft',
        due_date: '',
        validity_date: '',
        discount: '',
        tax: '',
        notes: doc.notes || '',
        name: doc.name || '',
        email: doc.email || '',
        phone: doc.phone || '',
        address: doc.address || '',
        company: doc.company || '',
      });
      setSelectedId(doc.id || '');
      return;
    }
    setSelectedId(doc.id || '');
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete this ${config.title.slice(0, -1).toLowerCase()}?`)) return;
    try {
      await api.delete(`/dashboard/${config.endpoint}/${doc.id}`);
      toast.success(`${config.title.slice(0, -1)} deleted`);
      if (selectedId === doc.id) resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${config.title.toLowerCase()}`);
    }
  };

  const openWhatsApp = async (doc) => {
    try {
      const endpoint = mode === 'invoice' ? 'invoices' : 'quotations';
      const res = await api.post(`/dashboard/${endpoint}/${doc.id}/share-whatsapp`);
      window.open(res.data?.whatsapp_url || `https://wa.me/?text=${encodeURIComponent(res.data?.message || '')}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create WhatsApp link');
    }
  };

  const convertQuotation = async (doc) => {
    try {
      const res = await api.post(`/dashboard/quotations/${doc.id}/convert`);
      toast.success(res.data?.message || 'Quotation converted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not convert quotation');
    }
  };

  const payInvoice = async (doc) => {
    try {
      const res = await api.post(`/dashboard/invoices/${doc.id}/checkout`);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Checkout URL was not returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start invoice payment');
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const renderForm = () => {
    if (!canEdit || mode === 'receipt') return null;
    if (mode === 'customer') {
      return (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Users size={18} /> Customer Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={save}>
              {[
                ['Name', 'name'],
                ['Company', 'company'],
                ['Phone', 'phone'],
                ['Email', 'email'],
                ['Address', 'address'],
              ].map(([label, key]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input value={form[key] || ''} onChange={(e) => updateForm(key, e.target.value)} className="mt-1" />
                </div>
              ))}
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes || ''} onChange={(e) => updateForm('notes', e.target.value)} className="mt-1 min-h-28" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                  {saving ? 'Saving...' : selectedId ? 'Update Customer' : 'Save Customer'}
                </Button>
                {selectedId ? <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button> : null}
              </div>
            </form>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <FileText size={18} /> {config.title} Form
          </CardTitle>
          <p className="text-sm text-slate-500">Create, send, share, convert and print without changing the current UI style.</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={save}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input value={form.title || ''} onChange={(e) => updateForm('title', e.target.value)} className="mt-1" placeholder={`Your ${config.title.slice(0, -1).toLowerCase()} title`} />
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input value={form.customer_name || ''} onChange={(e) => updateForm('customer_name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Customer Phone</Label>
                <Input value={form.customer_phone || ''} onChange={(e) => updateForm('customer_phone', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input value={form.customer_email || ''} onChange={(e) => updateForm('customer_email', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Customer Company</Label>
                <Input value={form.customer_company || ''} onChange={(e) => updateForm('customer_company', e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label>Customer Address</Label>
                <Textarea value={form.customer_address || ''} onChange={(e) => updateForm('customer_address', e.target.value)} className="mt-1 min-h-20" />
              </div>
              <div>
                <Label>Currency</Label>
                <Input value={form.currency || 'UGX'} onChange={(e) => updateForm('currency', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status || 'draft'} onValueChange={(value) => updateForm('status', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {config.statusOptions.map((s) => <SelectItem key={s} value={s}>{statusText(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {config.showDueDate ? (
                <div>
                  <Label>{mode === 'quotation' ? 'Validity Date' : 'Due Date'}</Label>
                  <Input type="date" value={mode === 'quotation' ? form.validity_date || '' : form.due_date || ''} onChange={(e) => updateForm(mode === 'quotation' ? 'validity_date' : 'due_date', e.target.value)} className="mt-1" />
                </div>
              ) : null}
              <div>
                <Label>Discount</Label>
                <Input type="number" value={form.discount || ''} onChange={(e) => updateForm('discount', e.target.value)} className="mt-1" min="0" step="0.01" />
              </div>
              <div>
                <Label>Tax</Label>
                <Input type="number" value={form.tax || ''} onChange={(e) => updateForm('tax', e.target.value)} className="mt-1" min="0" step="0.01" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <Label>{config.itemLabel}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {lineItems.map((item, index) => (
                  <div key={item.id || index} className="grid gap-3 md:grid-cols-12 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <div className="md:col-span-4">
                      <Label className="text-xs">Item Name</Label>
                      <Input value={item.name || ''} onChange={(e) => updateLine(index, 'name', e.target.value)} className="mt-1" />
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-xs">Description</Label>
                      <Input value={item.description || ''} onChange={(e) => updateLine(index, 'description', e.target.value)} className="mt-1" />
                    </div>
                    <div className="md:col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min="1" value={item.quantity || 1} onChange={(e) => updateLine(index, 'quantity', e.target.value)} className="mt-1" />
                    </div>
                    <div className="md:col-span-1">
                      <Label className="text-xs">Unit</Label>
                      <Input type="number" min="0" step="0.01" value={item.unit_price || ''} onChange={(e) => updateLine(index, 'unit_price', e.target.value)} className="mt-1" />
                    </div>
                    <div className="md:col-span-1">
                      <Label className="text-xs">Total</Label>
                      <Input value={item.total || ''} onChange={(e) => updateLine(index, 'total', e.target.value)} className="mt-1" />
                    </div>
                    <div className="md:col-span-1 flex items-end justify-end">
                      <Button type="button" variant="outline" size="icon" onClick={() => removeLine(index)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes || ''} onChange={(e) => updateForm('notes', e.target.value)} className="mt-1 min-h-28" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                {saving ? 'Saving...' : selectedId ? `Update ${config.title.slice(0, -1)}` : `Save ${config.title.slice(0, -1)}`}
              </Button>
              {selectedId ? <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button> : null}
            </div>
          </form>
        </CardContent>
      </Card>
    );
  };

  const renderList = () => {
    const emptyLabel = mode === 'customer' ? 'customers' : mode === 'receipt' ? 'receipts' : `${config.title.toLowerCase()}`;
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {mode === 'invoice' ? <FileText size={18} /> : mode === 'quotation' ? <ArrowRightLeft size={18} /> : mode === 'receipt' ? <ReceiptText size={18} /> : <Users size={18} />}
              {config.title} List
            </CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">{items.length} record(s)</p>
          </div>
          {canEdit && mode !== 'receipt' && mode !== 'customer' && (
            <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={resetForm}>
              <Plus size={14} className="mr-1" /> New {config.title.slice(0, -1)}
            </Button>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {items.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{mode === 'receipt' ? 'Receipt / Number' : mode === 'customer' ? 'Customer' : 'Number'}</TableHead>
                  <TableHead>{mode === 'customer' ? 'Contact' : 'Customer'}</TableHead>
                  {mode !== 'customer' && <TableHead>Title</TableHead>}
                  {mode !== 'customer' && <TableHead>Total</TableHead>}
                  {mode !== 'customer' && <TableHead>Status</TableHead>}
                  {mode === 'receipt' && <TableHead>Payment Method</TableHead>}
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((doc) => {
                  const publicUrl = doc.public_url || '';
                  return (
                    <TableRow key={doc.id || doc._id}>
                      <TableCell className="font-mono text-xs break-all">{mode === 'receipt' ? (doc.receipt_number || doc.id || '-') : (doc.invoice_number || doc.quotation_number || doc.name || doc.id || '-')}</TableCell>
                      <TableCell>
                        {mode === 'customer' ? (
                          <div>
                            <div className="font-medium text-slate-900">{doc.name || '-'}</div>
                            <div className="text-xs text-slate-500">{doc.phone || '-'} • {doc.email || '-'}</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium text-slate-900">{doc.customer?.name || doc.customer_name || '-'}</div>
                            <div className="text-xs text-slate-500">{doc.customer?.phone || doc.customer_phone || '-'}</div>
                          </div>
                        )}
                      </TableCell>
                      {mode !== 'customer' && <TableCell className="max-w-[280px]"><div className="font-medium text-slate-900">{doc.title || doc.name || '-'}</div><div className="text-xs text-slate-500">{doc.customer?.email || doc.customer_email || ''}</div></TableCell>}
                      {mode !== 'customer' && <TableCell>{formatMoney(doc.currency, doc.total || doc.amount || 0)}</TableCell>}
                      {mode !== 'customer' && <TableCell><Badge className={statusBadgeClass(doc.status)}>{statusText(doc.status)}</Badge></TableCell>}
                      {mode === 'receipt' && <TableCell className="text-slate-600">{doc.payment_method || '-'}</TableCell>}
                      <TableCell className="text-slate-500">{formatDate(doc.created_at || doc.issued_at || doc.updated_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {mode !== 'receipt' && (
                            <Button type="button" variant="outline" size="sm" onClick={() => editDoc(doc)}><Pencil size={14} className="mr-1" /> Edit</Button>
                          )}
                          {mode !== 'receipt' && canEdit && (
                            <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteDoc(doc)}><Trash2 size={14} className="mr-1" /> Delete</Button>
                          )}
                          {mode === 'invoice' && (
                            <>
                              <Button type="button" variant="outline" size="sm" onClick={() => payInvoice(doc)}><CheckCircle2 size={14} className="mr-1" /> {config.primaryAction}</Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => openWhatsApp(doc)}><MessageCircle size={14} className="mr-1" /> {config.secondaryAction}</Button>
                            </>
                          )}
                          {mode === 'quotation' && (
                            <>
                              <Button type="button" variant="outline" size="sm" onClick={() => convertQuotation(doc)}><ArrowRightLeft size={14} className="mr-1" /> Convert</Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => openWhatsApp(doc)}><MessageCircle size={14} className="mr-1" /> {config.secondaryAction}</Button>
                            </>
                          )}
                          {(mode === 'invoice' || mode === 'quotation' || mode === 'receipt') && publicUrl && (
                            <>
                              <Button type="button" variant="outline" size="sm" onClick={() => copyLink(publicUrl)}><Copy size={14} className="mr-1" /> Copy</Button>
                              <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex">
                                <Button type="button" variant="outline" size="sm">
                                  <Share2 size={14} className="mr-1" /> Open
                                </Button>
                              </a>
                            </>
                          )}
                          {mode === 'customer' && (
                            <>
                              <Button type="button" variant="outline" size="sm" onClick={() => editDoc(doc)}><Pencil size={14} className="mr-1" /> Edit</Button>
                              <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteDoc(doc)}><Trash2 size={14} className="mr-1" /> Delete</Button>
                            </>
                          )}
                          {mode === 'receipt' && publicUrl && (
                            <>
                              <Button type="button" variant="outline" size="sm" onClick={() => copyLink(publicUrl)}><Copy size={14} className="mr-1" /> Copy</Button>
                              <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex">
                                <Button type="button" variant="outline" size="sm"><Printer size={14} className="mr-1" /> Print</Button>
                              </a>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center text-sm text-slate-500">No {emptyLabel} yet.</div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.title}</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          {mode === 'invoice' ? 'Create and send invoices, collect payment, and generate receipts.' :
            mode === 'quotation' ? 'Create quotations and convert them into invoices when accepted.' :
            mode === 'receipt' ? 'View generated receipts and print or share them.' :
            'Create and manage your customer list for invoices, quotations, and follow-ups.'}
        </p>
      </div>

      {canEdit ? null : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Your current package does not allow this feature. Upgrade your package to continue.
        </div>
      )}

      {renderForm()}
      {renderList()}
    </div>
  );
}
